// src/components/PhaserBoard.jsx
import React, { useEffect, useRef } from 'react';
import Phaser from 'phaser';

export default function PhaserBoard({
  rows = 9,
  cols = 9,
  cellSize = 64,
  minCellSize = 28,
  board = [],
  lastMove = null,
  pieceLabels = {},
  promotedLabels = {},
  selected = null,
  legalTargets = [],
  onSquareClick = () => {},
  flipBoard = false,

  flatBoard = true,
  lightColor = 0xf0d9b5,
  darkColor = 0xb58863,
  boardColor = 0xdcb35c,
  lineColor = 0x3b2a1a,

  komaFillColor = 0xf7dfa5,
  komaStrokeColor = 0x6b4423,
  komaStrokeWidth = 2,
  komaScale = 0.86,
  inkColor = '#1a1008',
  promotedInkColor = '#b5121b',
  goteInkColor = null,
  goteInkColorPromoted = null,

  highlightColor = 0x2ecc71,
  selectColor = 0xf1c40f,
}) {
  const containerRef = useRef(null);
  const gameRef = useRef(null);
  const sceneRef = useRef(null);
  const stateRef = useRef({});

  stateRef.current = { selected, legalTargets, onSquareClick, flipBoard };

  useEffect(() => {
    let isMounted = true;
    let animationFrameId = null;

    const width = cols * cellSize;
    const height = rows * cellSize;

    class BoardScene extends Phaser.Scene {
      constructor() {
        super('BoardScene');
      }

      create() {
        this.gridGraphics = this.add.graphics();
        this.highlightGraphics = this.add.graphics();
        this.pieceGrid = Array.from({ length: rows }, () => Array(cols).fill(null));

        const w = cellSize * komaScale * 0.82;
        const h = cellSize * komaScale;

        this.komaPoints = [
          0, -h / 2,
          w * 0.30, -h * 0.30,
          w * 0.40, h / 2,
          -w * 0.40, h / 2,
          -w * 0.30, -h * 0.30,
        ];
        this.komaTextYOffset = this._polygonCentroidY(this.komaPoints);

        for (let r = 0; r < rows; r++) {
          for (let c = 0; c < cols; c++) {
            const zone = this.add
              .zone(c * cellSize, r * cellSize, cellSize, cellSize)
              .setOrigin(0, 0)
              .setInteractive({ useHandCursor: true });

            zone.on('pointerdown', () => {
              const isFlipped = stateRef.current.flipBoard;
              const actualRow = isFlipped ? rows - 1 - r : r;
              const actualCol = isFlipped ? cols - 1 - c : c;
              stateRef.current.onSquareClick(actualRow, actualCol);
            });
          }
        }

        this.drawBoard();
        this.fullRenderPieces(board);
        this.drawHighlights();

        if (this.sys.game.canvas) {
          this.sys.game.canvas.style.touchAction = 'manipulation';
        }
      }

      drawBoard() {
        const g = this.gridGraphics;
        g.clear();
        if (flatBoard) {
          g.fillStyle(boardColor, 1);
          g.fillRect(0, 0, cols * cellSize, rows * cellSize);
        } else {
          for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
              const isLight = (r + c) % 2 === 0;
              g.fillStyle(isLight ? lightColor : darkColor, 1);
              g.fillRect(c * cellSize, r * cellSize, cellSize, cellSize);
            }
          }
        }
        g.lineStyle(2, lineColor, 1);
        for (let i = 0; i <= cols; i++) g.lineBetween(i * cellSize, 0, i * cellSize, rows * cellSize);
        for (let i = 0; i <= rows; i++) g.lineBetween(0, i * cellSize, cols * cellSize, i * cellSize);
      }

      _polygonCentroidY(points) {
        let area = 0;
        let cy = 0;
        const n = points.length / 2;
        for (let i = 0; i < n; i++) {
          const x0 = points[i * 2];
          const y0 = points[i * 2 + 1];
          const j = (i + 1) % n;
          const x1 = points[j * 2];
          const y1 = points[j * 2 + 1];
          const cross = x0 * y1 - x1 * y0;
          area += cross;
          cy += (y0 + y1) * cross;
        }
        area *= 0.5;
        if (Math.abs(area) < 1e-6) return 0;
        return cy / (6 * area);
      }

      _labelFor(piece) {
        const rawLabel =
          piece.promoted && promotedLabels[piece.type]
            ? promotedLabels[piece.type]
            : pieceLabels[piece.type] || piece.type;
        let displayLabel = rawLabel;
        let fontSize = cellSize * 0.36;
        if (rawLabel.length === 2) fontSize = cellSize * 0.27;
        if (rawLabel.length >= 3) {
          displayLabel = rawLabel[0] + '\n' + rawLabel.slice(1);
          fontSize = cellSize * 0.24;
        }
        return { displayLabel, fontSize };
      }

      _inkFor(piece) {
        const isGote = piece.color === 'gote';
        return piece.promoted
          ? isGote && goteInkColorPromoted ? goteInkColorPromoted : promotedInkColor
          : isGote && goteInkColor ? goteInkColor : inkColor;
      }

      createPieceContainer(x, y, piece) {
        const cont = this.add.container(x, y);

        const g = this.add.graphics();
        g.fillStyle(komaFillColor, 1);
        g.lineStyle(komaStrokeWidth, komaStrokeColor, 1);
        g.beginPath();
        g.moveTo(this.komaPoints[0], this.komaPoints[1]);
        for (let i = 2; i < this.komaPoints.length; i += 2) {
          g.lineTo(this.komaPoints[i], this.komaPoints[i + 1]);
        }
        g.closePath();
        g.fillPath();
        g.strokePath();

        const { displayLabel, fontSize } = this._labelFor(piece);
        const txt = this.add
          .text(0, this.komaTextYOffset, displayLabel, {
            fontSize: `${Math.floor(fontSize)}px`,
            color: this._inkFor(piece),
            fontStyle: 'bold',
            align: 'center',
            fontFamily: '"Hiragino Sans", "Yu Gothic", "Noto Sans JP", Roboto, sans-serif',
          })
          .setOrigin(0.5);

        cont.add([g, txt]);

        const isFlipped = stateRef.current.flipBoard;
        const isBottomPiece = isFlipped ? (piece.color === 'gote') : (piece.color === 'sente');

        cont.setAngle(isBottomPiece ? 0 : 180);
        return cont;
      }

      fullRenderPieces(currentBoard) {
        for (let r = 0; r < rows; r++) {
          for (let c = 0; c < cols; c++) {
            if (this.pieceGrid[r][c]) {
              this.pieceGrid[r][c].destroy();
              this.pieceGrid[r][c] = null;
            }
          }
        }

        const isFlipped = stateRef.current.flipBoard;

        for (let r = 0; r < rows; r++) {
          for (let c = 0; c < cols; c++) {
            const piece = currentBoard?.[r]?.[c];
            if (!piece) continue;

            const renderR = isFlipped ? rows - 1 - r : r;
            const renderC = isFlipped ? cols - 1 - c : c;

            const x = renderC * cellSize + cellSize / 2;
            const y = renderR * cellSize + cellSize / 2;
            this.pieceGrid[r][c] = this.createPieceContainer(x, y, piece);
          }
        }
      }

      drawHighlights() {
        const g = this.highlightGraphics;
        g.clear();
        const { selected, legalTargets, flipBoard } = stateRef.current;

        if (selected) {
          const r = flipBoard ? rows - 1 - selected.row : selected.row;
          const c = flipBoard ? cols - 1 - selected.col : selected.col;
          g.fillStyle(selectColor, 0.45);
          g.fillRect(c * cellSize, r * cellSize, cellSize, cellSize);
        }

        (legalTargets || []).forEach(({ row, col }) => {
          const r = flipBoard ? rows - 1 - row : row;
          const c = flipBoard ? cols - 1 - col : col;
          g.fillStyle(highlightColor, 0.45);
          g.fillCircle(c * cellSize + cellSize / 2, r * cellSize + cellSize / 2, cellSize * 0.15);
        });
      }
    }

    const initPhaser = () => {
      if (!isMounted) return;

      if (!containerRef.current || containerRef.current.clientWidth === 0) {
        animationFrameId = requestAnimationFrame(initPhaser);
        return;
      }

      const config = {
        type: Phaser.AUTO,
        width,
        height,
        parent: containerRef.current,
        transparent: true,
        scene: BoardScene,
        scale: {
          mode: Phaser.Scale.FIT,
          autoCenter: Phaser.Scale.CENTER_HORIZONTALLY,
          width,
          height,
          min: { width: cols * minCellSize, height: rows * minCellSize },
          max: { width, height },
        },
        input: {
          activePointers: 1,
          touch: { capture: true },
        },
      };

      const game = new Phaser.Game(config);
      gameRef.current = game;

      game.events.once(Phaser.Core.Events.READY, () => {
        if (!isMounted) return;
        sceneRef.current = game.scene.getScene('BoardScene');
        if (sceneRef.current) {
          sceneRef.current.fullRenderPieces(board);
          sceneRef.current.drawHighlights();
        }
      });
    };

    initPhaser();

    return () => {
      isMounted = false;
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
      if (gameRef.current) {
        gameRef.current.destroy(true);
        gameRef.current = null;
        sceneRef.current = null;
      }
    };
  }, [rows, cols, cellSize, minCellSize]);

  useEffect(() => {
    if (sceneRef.current) {
      sceneRef.current.fullRenderPieces(board);
      sceneRef.current.drawHighlights();
    }
  }, [board, flipBoard, lastMove]);

  useEffect(() => {
    sceneRef.current?.drawHighlights();
  }, [selected, legalTargets]);

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        maxWidth: cols * cellSize,
        margin: '0 auto',
        aspectRatio: `${cols} / ${rows}`,
        touchAction: 'manipulation',
      }}
    />
  );
}